/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { useEffect, useRef } from 'react';
import { useConfig } from '@salesforce/storefront-next-runtime/config';

declare global {
    interface Window {
        initSendOTP?: (options: Record<string, unknown>) => void;
        sendOtp?: (identifier: string, onSuccess: (data: any) => void, onError: (err: any) => void) => void;
        verifyOtp?: (
            code: string,
            onSuccess: (data: any) => void,
            onError: (err: any) => void,
            reqId?: string | null
        ) => void;
        retryOtp?: (
            channel: string,
            onSuccess: (data: any) => void,
            onError: (err: any) => void,
            reqId?: string | null
        ) => void;
    }
}

const MAX_RESEND_ATTEMPTS = 3;

// Masks a phone/email identifier for display, e.g. "919876543210" -> "+91 ******3210",
// "jane.doe@example.com" -> "j***e@example.com"
export function maskMsg91Identifier(identifier: string): string {
    if (identifier.includes('@')) {
        const [user, domain] = identifier.split('@');
        if (user.length <= 2) return `${user[0]}***@${domain}`;
        return `${user[0]}${'*'.repeat(Math.max(user.length - 2, 3))}${user[user.length - 1]}@${domain}`;
    }
    const digits = identifier.replace(/\D/g, '');
    if (digits.length <= 4) return digits;
    const countryCode = digits.slice(0, digits.length - 10) || digits.slice(0, 2);
    const last4 = digits.slice(-4);
    const maskedLength = digits.length - countryCode.length - 4;
    return `+${countryCode} ${'*'.repeat(Math.max(maskedLength, 0))}${last4}`;
}

// Normalizes a 10-digit phone number to prepend '91' and strips a leading '+'.
/**
 * Wrong / expired code errors from MSG91 (e.g. "invalid otp") all read "Please enter valid OTP", matching the
 * form validation wording. Attempt-limit and blocked messages are kept as-is — the OTP popup relies on them to lock.
 */
export function toOtpErrorMessage(message?: string | null): string {
    const text = message?.trim() ?? '';
    if (/limit|exceed|too many|maximum|max attempts|block|blacklist|suspend/i.test(text)) return text;
    return 'Please enter valid OTP';
}

/**
 * MSG91 widget channel codes for retryOtp — the widget throws "Channel not provided" without one.
 * SMS ('11') for phone numbers, email ('3') for email addresses.
 */
export function getMsg91RetryChannel(identifier: string): string {
    return identifier.includes('@') ? '3' : '11';
}

export function normalizeMsg91Identifier(value: string): string {
    const cleanDigits = value.replace(/\D/g, '');
    if (/^\d{10}$/.test(cleanDigits)) {
        return `91${cleanDigits}`;
    }
    if (value.startsWith('+')) {
        return value.substring(1);
    }
    return value;
}

/**
 * Loads the MSG91 widget script and exposes its send/verify/resend OTP methods.
 * Shared by any flow (login page, checkout, header sign-in) that needs the custom
 * MSG91 popup UI instead of MSG91's own hosted widget (exposeMethods:true suppresses it).
 */
export function useMsg91Otp() {
    const config = useConfig();
    const widgetInitializedRef = useRef(false);
    const reqIdRef = useRef<string | null>(null);
    // Identifier the OTP was last sent to — decides the resend channel (SMS vs email)
    const identifierRef = useRef<string>('');
    const resendCountRef = useRef(0);

    useEffect(() => {
        if (typeof window === 'undefined' || window.initSendOTP) return;

        const urls = ['https://verify.msg91.com/otp-provider.js', 'https://verify.phone91.com/otp-provider.js'];
        let i = 0;
        function attempt() {
            const s = document.createElement('script');
            s.src = urls[i];
            s.async = true;
            s.onerror = () => {
                i++;
                if (i < urls.length) attempt();
            };
            document.head.appendChild(s);
        }
        attempt();
    }, []);

    const sendOtp = (identifier: string, onSuccess: () => void, onError: (message: string) => void) => {
        const widgetId = config?.features?.msg91?.widgetId || '3666656a4470343931353433';
        const tokenAuth = config?.features?.msg91?.tokenAuth || '523025TVi0B5Cp4166a22a5caP1';
        const w = window;

        const callSendOtp = () => {
            identifierRef.current = identifier;
            w.sendOtp?.(
                identifier,
                (data: any) => {
                    reqIdRef.current = data?.message || data?.reqId || null;
                    resendCountRef.current = 0;
                    onSuccess();
                },
                (err: any) => {
                    onError(err?.message || 'Failed to send OTP.');
                }
            );
        };

        const initWidgetAndSend = () => {
            if ((w as any).Zone?.assertZonePatched) {
                (w as any).Zone.assertZonePatched = () => {};
            }
            w.initSendOTP?.({
                widgetId,
                tokenAuth,
                exposeMethods: true,
                success: () => {},
                failure: () => {},
            });
            widgetInitializedRef.current = true;

            // initSendOTP does async setup before attaching window.sendOtp, so it isn't
            // guaranteed to be ready on this same tick — poll briefly.
            let attempts = 0;
            const waitForSendOtp = () => {
                if (typeof w.sendOtp === 'function') {
                    callSendOtp();
                } else if (attempts < 20) {
                    attempts++;
                    setTimeout(waitForSendOtp, 100);
                } else {
                    onError('Failed to initialize OTP widget. Please try again.');
                }
            };
            waitForSendOtp();
        };

        if (widgetInitializedRef.current) {
            callSendOtp();
        } else if (typeof w.initSendOTP === 'function') {
            initWidgetAndSend();
        } else {
            // The otp-provider.js script (loaded async on mount) may not have finished
            // downloading/executing yet, especially on slower prod networks — wait for
            // window.initSendOTP to appear instead of silently no-oping and getting stuck.
            let scriptAttempts = 0;
            const waitForScript = () => {
                if (typeof w.initSendOTP === 'function') {
                    initWidgetAndSend();
                } else if (scriptAttempts < 50) {
                    scriptAttempts++;
                    setTimeout(waitForScript, 100);
                } else {
                    onError('Failed to initialize OTP widget. Please try again.');
                }
            };
            waitForScript();
        }
    };

    const verifyOtp = (code: string, onSuccess: (accessToken: string) => void, onError: (message: string) => void) => {
        window.verifyOtp?.(
            code,
            (data: any) => {
                onSuccess(data?.message || data?.data || data);
            },
            (err: any) => {
                onError(toOtpErrorMessage(err?.message));
            },
            reqIdRef.current
        );
    };

    const resendOtp = (onError: (message: string) => void) => {
        try {
        if (resendCountRef.current >= MAX_RESEND_ATTEMPTS) {
            onError('You have reached the maximum number of resend attempts.');
            return;
        }

        resendCountRef.current += 1;

        window.retryOtp?.(
            getMsg91RetryChannel(identifierRef.current),
            (data: any) => {
                resendCountRef.current += 1;
                if (data?.message || data?.reqId) {
                    reqIdRef.current = data.message || data.reqId;
                }
            },
            (err: any) => {
                onError(err?.message || 'Failed to resend code. Please try again.');
            },
            reqIdRef.current
        );
        } catch (err: any) {
            // The widget throws (rather than calling onError) for invalid input — surface it instead of failing silently
            onError(err?.message || 'Failed to resend code. Please try again.');
        }
    };

    const getResendAttemptsRemaining = () => Math.max(MAX_RESEND_ATTEMPTS - resendCountRef.current, 0);
    return { sendOtp, verifyOtp, resendOtp, getResendAttemptsRemaining };
}
